import { useCallback, useEffect, useMemo, useState } from "react";
import { useBorrowerSubmissionsHook } from "../../hooks/useBorrowerSubmissionsHook";
import { LANES } from "./submissionHelpers";

/**
 * The unredacted submission, fetched by id.
 *
 * The list endpoints project out `borrowerSSN`, `dateOfBirth`, `signature` and
 * the raw payload — lendmarq-os `loan_applications.py` LIST_SENSITIVE_FIELDS,
 * applied in `_SubmissionStore.list_for_grid`. So a grid row can never carry
 * them, no matter what the borrower submitted. The detail reads
 * (`/application/{id}`, `/credit_authorization/{id}`) apply no projection.
 *
 * Anything that renders or PRINTS one submission has to go through here, or it
 * silently produces a redacted artifact. The row's download button and the
 * expanded panel's both do — that's why the fetch lives in its own module
 * rather than inside the panel.
 *
 * Merged OVER the row, not swapped for it: `__type` is stamped client-side on
 * the way into the grid and the detail read knows nothing about it. Same
 * document either way, so the merge only ever fills fields back in.
 */
export const useFetchFullSubmission = () => {
  const { getApplicationById, getCreditAuthorizationById } =
    useBorrowerSubmissionsHook();

  return useCallback(
    (row) =>
      new Promise((resolve) => {
        if (!row?._id) return resolve(row);
        const fetchById =
          row.__type === LANES.CREDIT_AUTHORIZATION
            ? getCreditAuthorizationById
            : getApplicationById;

        fetchById({
          appId: row._id,
          // `__fullRecord` lets a renderer tell "stripped by the list read" from
          // "absent from the submission itself".
          onSuccessFn: (doc) =>
            resolve(doc ? { ...row, ...doc, __fullRecord: true } : row),
          // Resolve with the redacted row rather than rejecting: every consumer
          // already renders without these fields, so a failed fetch degrades to
          // what the screen showed before this existed instead of breaking.
          onFailFn: () => resolve(row),
        });
      }),
    // The hook rebuilds its functions every render; the identities it closes
    // over are stable enough for a one-shot fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
};

/** The same fetch as state, for a panel that wants it as soon as it opens. */
export const useFullSubmission = (row) => {
  const fetchFull = useFetchFullSubmission();
  // Keyed by id so a row swap can't merge the previous row's PII into this one.
  const [detail, setDetail] = useState(null);

  const id = row?._id;
  const type = row?.__type;

  useEffect(() => {
    if (!id) return undefined;
    let live = true;
    fetchFull(row).then((full) => live && full && setDetail({ id, doc: full }));
    return () => {
      live = false;
    };
    // Refetch only for a new row, not for every re-render of the same one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, type]);

  return useMemo(
    () => (detail?.id === id ? { ...row, ...detail.doc } : row),
    [row, detail, id],
  );
};

export default useFullSubmission;
